/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Divider, Icon, Collapse } from '@mui/material';
import { get, map, size } from 'global/lib/lodash';
import clsx from 'clsx';
import {
  HeaderChild,
  SidebarHeaderChild,
  ChecklistMenuHeader,
  ListItemWrapper,
  ListItemWrapChild,
  ExpandIcon,
  ChecklistActiveButton,
  SystemActiveButton,
  LeftSidebar,
  LeftSidebarMobile,
  OverallScoreItem,
  SidebarListCard,
  SIDEBAR_SECTION_NAME,
  NA_SCORE_CLASS,
  SCORE_VALUE_CLASS,
  TaskBadge,
} from './styles';
import Skeleton from '@mui/material/Skeleton';
import { useScreen } from 'global/styles';
import { findTaskForItem, getId, isTaskItem } from './index';
import { displayText } from '../../../global/components/ScoreIcon';
import { getOverallScoreDisplay, scrollChecklistHeaderIntoView } from './utils';

const SideBarList = (props) => {
  const { isCurrent, pssvSystem, setSidebarOpen, hash, setHash, setActiveEdit, activeEdit, canShowScores, filter, assessmentTasks } = props;
  const [open, setOpen] = useState(isCurrent);

  const toggleOpen = (bool) => {
    setOpen(bool);
  };

  useEffect(() => {
    toggleOpen(isCurrent);
  }, [isCurrent]);

  const onClick = useCallback(
    (c) => {
      setHash(getId(c));
      if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
        setActiveEdit({
          pssvChecklist: c,
          pssvSystem,
        });
      }
    },
    [activeEdit]
  );

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let sysVal = useCompareScore ? get(pssvSystem, 'compare.displayValue') : pssvSystem.displayValue;
  if (useCompareScore && useDiff) {
    sysVal = displayText(pssvSystem, pssvSystem.compare, props);
  }
  const scoreIsNa = (item) => (useCompareScore ? get(item, 'compare.value') : get(item, 'value')) === null;

  const tasks = assessmentTasks || [];
  const checklistHasTasks = (checklist) => {
    const children = get(checklist, 'children', []);
    return children.some((cp) => !!findTaskForItem(cp, tasks));
  };

  const systemHasTasks = get(props, 'pssvChecklists', []).some(checklistHasTasks);

  return (
    <>
      <SystemActiveButton selected={!!isCurrent} onClick={() => toggleOpen(!open)}>
        {systemHasTasks && <TaskBadge />}
        <ListItemWrapper>
          <HeaderChild className={SIDEBAR_SECTION_NAME}>{pssvSystem.name}</HeaderChild>
          {canShowScores && (
            <SidebarHeaderChild as={HeaderChild} className={clsx(scoreIsNa(pssvSystem) && NA_SCORE_CLASS)}>{sysVal}</SidebarHeaderChild>
          )}
          <ExpandIcon as={Icon}>{open ? 'expand_less' : 'expand_more'}</ExpandIcon>
        </ListItemWrapper>
      </SystemActiveButton>
      <Collapse in={open} timeout={150}>
        <div onClick={() => setSidebarOpen(false)}>
          {get(props, 'pssvChecklists', []).map((pssvChecklist) => {
            const id = getId(pssvChecklist).toString();
            let checkVal = useCompareScore ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
            if (useCompareScore && useDiff) {
              checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
            }
            return (
              <ChecklistActiveButton
                key={id}
                selected={hash === id}
                onClick={() => onClick(pssvChecklist)}
              >
                {checklistHasTasks(pssvChecklist) && <TaskBadge />}
                <ChecklistMenuHeader className={SIDEBAR_SECTION_NAME}>{pssvChecklist.name}</ChecklistMenuHeader>
                {canShowScores && (
                  <ChecklistMenuHeader className={clsx(SCORE_VALUE_CLASS, scoreIsNa(pssvChecklist) && NA_SCORE_CLASS)}>{checkVal}</ChecklistMenuHeader>
                )}
              </ChecklistActiveButton>
            );
          })}
        </div>
      </Collapse>
      <Divider />
    </>
  );
};

export default (props) => {
  const { pssvSystems, processedOverallScores, loading, open, setSidebarOpen, setActiveEdit, activeEdit, canShowScores, scorecardIndex, filter, assessmentTasks, currentAnchor } = props;
  const { isMobile } = useScreen();
  const [hash, setHash] = useState();
  // Sidebar click pins selection until the user scrolls (observer often lands on a
  // different section when checklists are collapsed / all NA).
  const pinnedRef = useRef(false);
  const ignoreScrollUntil = useRef(0);
  // Mobile Drawer restores window scroll when it closes; queue the target until then.
  const pendingScrollId = useRef(null);
  // PFM-2601: When only one superset (system) exists, the superset accordion is
  // visually redundant (its score always equals the overall score). Render the
  // checklists as a flat list instead.
  const isSingleSystem = size(pssvSystems) === 1;

  const scrollToId = (id) => {
    scrollChecklistHeaderIntoView(id);
    ignoreScrollUntil.current = Date.now() + 150;
  };

  const flushPendingScroll = useCallback(() => {
    const id = pendingScrollId.current;
    if (id == null) return;
    pendingScrollId.current = null;
    requestAnimationFrame(() => {
      scrollChecklistHeaderIntoView(id);
      ignoreScrollUntil.current = Date.now() + 150;
    });
  }, []);

  const sidebarSetHash = useCallback(
    (h) => {
      pinnedRef.current = true;
      setHash(h);
      if (activeEdit) return;
      if (isMobile) {
        pendingScrollId.current = h;
        ignoreScrollUntil.current = Date.now() + 1000;
        // Fallback if Slide onExited does not fire (styled Drawer / reduced motion).
        window.setTimeout(flushPendingScroll, 300);
      } else {
        scrollToId(h);
      }
    },
    [activeEdit, flushPendingScroll, isMobile]
  );

  const DRAW = isMobile ? LeftSidebarMobile : LeftSidebar;
  const drawerProps = isMobile
    ? {
        // Without this, closing the temporary Drawer restores the pre-open scroll
        // position and undoes navigation — especially on first load at scrollY 0.
        disableScrollLock: true,
        slotProps: { transition: { onExited: flushPendingScroll } },
      }
    : {};

  // Sync sidebar highlight with the currently visible section (from IntersectionObserver)
  useEffect(() => {
    if (currentAnchor && !pinnedRef.current) {
      setHash(currentAnchor);
    }
  }, [currentAnchor]);

  useEffect(() => {
    const onScroll = () => {
      if (pinnedRef.current && Date.now() >= ignoreScrollUntil.current) {
        pinnedRef.current = false;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    try {
      if (activeEdit) {
        pinnedRef.current = false;
        setHash(getId(get(activeEdit, 'pssvChecklist')));
      } else if (hash) {
        // triggers when user exits Edit Mode: scroll to the item just edited
        scrollToId(hash);
      }
    } catch (err) {
      console.error(err);
    }
  }, [activeEdit]);

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  const thisscore = get(processedOverallScores, scorecardIndex);
  let overall;
  if (useCompareScore && useDiff) {
    overall = displayText(thisscore, get(thisscore, 'compare'), props);
  } else {
    overall = getOverallScoreDisplay(useCompareScore ? get(thisscore, 'compare') : thisscore);
  }

  const useCompareScoreFlat = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiffFlat = get(filter, 'useDiff', false);
  const flatChecklistTasks = assessmentTasks || [];
  const flatChecklistHasTasks = (checklist) => {
    const children = get(checklist, 'children', []);
    return children.some((cp) => !!findTaskForItem(cp, flatChecklistTasks));
  };
  const onFlatClick = (c, sys) => {
    sidebarSetHash(getId(c));
    if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
      setActiveEdit({
        pssvChecklist: c,
        pssvSystem: sys,
      });
    }
  };

  return (
    <DRAW open={open} onClose={() => setSidebarOpen(false)} {...drawerProps}>
      {(loading || !size(pssvSystems)) && <Skeleton variant="rectangular" height={2000} />}
      <List>
        <OverallScoreItem>
          {`Overall:`}
          <span data-testid="overall-score" style={{ marginLeft: 2 }}>
            {overall}
          </span>
        </OverallScoreItem>
        <SidebarListCard>
          {isSingleSystem
            ? (() => {
                // PFM-2601: Single-superset scorecard — render the checklists flat,
                // skipping the redundant superset accordion entirely.
                const onlySystem = get(pssvSystems, 0);
                const checklists = get(onlySystem, 'children', []);
                // PFM-2601: Wrap the flat checklist buttons in a display:block
                // container (mirrors the accordion path's ShowWrap) so that
                // ChecklistActiveButton's `height: 100%` resolves against an
                // auto-height parent instead of stretching to fill the List flex
                // column, which made the rows render at full height.
                return (
                  <div onClick={() => setSidebarOpen(false)}>
                    {checklists.map((pssvChecklist) => {
                      const id = getId(pssvChecklist).toString();
                      let checkVal = useCompareScoreFlat ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
                      if (useCompareScoreFlat && useDiffFlat) {
                        checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
                      }
                      const checkIsNa = (useCompareScoreFlat ? get(pssvChecklist, 'compare.value') : get(pssvChecklist, 'value')) === null;
                      return (
                        <ChecklistActiveButton
                          key={id}
                          selected={hash === id}
                          onClick={() => onFlatClick(pssvChecklist, onlySystem)}
                        >
                          {flatChecklistHasTasks(pssvChecklist) && <TaskBadge />}
                          <ChecklistMenuHeader className={SIDEBAR_SECTION_NAME}>{pssvChecklist.name}</ChecklistMenuHeader>
                          {canShowScores && (
                            <ChecklistMenuHeader className={clsx(SCORE_VALUE_CLASS, checkIsNa && NA_SCORE_CLASS)}>{checkVal}</ChecklistMenuHeader>
                          )}
                        </ChecklistActiveButton>
                      );
                    })}
                  </div>
                );
              })()
            : map(pssvSystems, (sys, i) => {
                const isCurrent = hash && (getId(sys).toString() === hash || get(sys, 'children', []).find((ps) => getId(ps).toString() === hash));
                return (
                  <SideBarList
                    key={i}
                    filter={filter}
                    pssvSystem={sys}
                    pssvChecklists={sys.children}
                    setSidebarOpen={setSidebarOpen}
                    isCurrent={isCurrent}
                    hash={hash}
                    setHash={sidebarSetHash}
                    setActiveEdit={setActiveEdit}
                    activeEdit={activeEdit}
                    canShowScores={canShowScores}
                    scorecard={props.scorecard}
                    assessmentTasks={assessmentTasks}
                  />
                );
              })}
        </SidebarListCard>
      </List>
    </DRAW>
  );
};
